import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ClapperboardIcon } from 'lucide-react';
import { ProductionCard } from './ProductionCard';
import { pagePadding } from '../utils/styles';
import type { Production } from '../types/production';
interface CatalogRowProps {
  id: string;
  title: string;
  productions: Production[];
  emptyMessage: string;
}
export function CatalogRow({
  id,
  title,
  productions,
  emptyMessage
}: CatalogRowProps) {
  const scrollerRef = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrows();
    const observer = new ResizeObserver(updateArrows);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateArrows, productions.length]);
  const scrollPage = (direction: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({
      left: direction * el.clientWidth * 0.85,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const arrowClass = 'absolute bottom-5 top-5 z-10 hidden w-12 items-center justify-center bg-night/75 text-white opacity-0 transition-opacity duration-150 ease-out hover:bg-night/90 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white group-hover/row:opacity-100 md:flex';
  return <section aria-labelledby={`${id}-title`} data-id="element-107" data-mp="lix3odj47o6">
      <div className={pagePadding} data-id="element-108" data-mp="1zv91quboz7t0">
        <h2 id={`${id}-title`} className="font-display text-lg font-bold text-white md:text-xl" data-id="element-109" data-mp="1zu7nkgzdq572">
          {title}
        </h2>
      </div>

      {productions.length === 0 ? <div className={`mt-4 ${pagePadding}`} data-id="element-110" data-mp="2e09dyp1x7ohw">
          <div className="flex items-center gap-4 rounded-md border border-dashed border-graphite px-5 py-6" data-id="element-111" data-mp="2isalxys22t24">
            <ClapperboardIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-mist" data-id="element-112" data-mp="251nvetwg89h4" />
            <p className="text-sm text-mist" data-id="element-113" data-mp="29zz5n5qnolzv">{emptyMessage}</p>
          </div>
        </div> : <div className="group/row relative" data-id="element-114" data-mp="2xpa0k6lbrm63">
          {canPrev && <button type="button" onClick={() => scrollPage(-1)} aria-label={`Anterior en ${title}`} className={`${arrowClass} left-0 rounded-r-md`} data-id="element-115" data-mp="2xnyycb7j2mh3">
              <ChevronLeftIcon className="h-7 w-7" data-id="element-116" data-mp="2nqkiodtfi1pu" />
            </button>}
          <ul ref={scrollerRef} onScroll={updateArrows} className={`scrollbar-none flex snap-x snap-mandatory gap-3 overflow-x-auto py-5 md:gap-4 ${pagePadding} scroll-px-5 md:scroll-px-10 lg:scroll-px-14`} data-id="element-117" data-mp="2spxevp66wl53">
            {productions.map(p => <li key={p.id} className="w-[74vw] shrink-0 snap-start sm:w-[44vw] md:w-[31vw] lg:w-[24vw] xl:w-[19vw]" data-id="element-118" data-mp="19rixcces9vp1">
                <ProductionCard production={p} data-id="element-119" data-mp="14vabyh9zt69f" />
              </li>)}
          </ul>
          {canNext && <button type="button" onClick={() => scrollPage(1)} aria-label={`Siguiente en ${title}`} className={`${arrowClass} right-0 rounded-l-md`} data-id="element-120" data-mp="1dtvw2gv44tcb">
              <ChevronRightIcon className="h-7 w-7" data-id="element-121" data-mp="q4b97mvu9bxr" />
            </button>}
        </div>}
    </section>;
}